import { useEffect, useRef, useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useLocation } from "react-router";
import { BACKUP_CODE_RE, TOTP_CODE_RE, normalizeTotpCode, type TwoFactorStatusResponse } from "@townsy/contracts";
import { errorMessage } from "../../api/client";
import { postTwoFactorDisable, twoFactorApiMessage, twoFactorKeys, useTwoFactorStatusQuery } from "../../api/twoFactor";
import { useInvalidateIdentity } from "../../api/queries";
import { confirmTwoFactorSetup, startTwoFactorSetup, verifySignInBackupCode, verifySignInCode } from "../../auth/authClient";
import { formatUtcDateTime } from "../../lib/format";
import { QrCode } from "./QrCode";

/**
 * Account page: "Two-step sign-in" (ADR-0039). Better Auth's two-factor plugin with an authenticator app (TOTP):
 *   1. password -> the server returns a new secret (as an otpauth URI: shown as a QR code AND as a key to type) and ten
 *      backup codes;
 *   2. the first 6-digit code from the app turns it on (the server replaces the session);
 *   3. the backup codes are shown ONCE, with copy and download, until the person says they saved them.
 * Turning it off needs the password and a current code (or an unused backup code). Optional for residents; required
 * for staff tools. Everything is decided by the server.
 */

/** The base32 key of an otpauth URI, in groups of four for typing ("ABCD EFGH ..."). */
export function manualKeyFromUri(uri: string): string {
  try {
    const secret = new URL(uri).searchParams.get("secret") ?? "";
    return secret.replace(/(.{4})/g, "$1 ").trim();
  } catch {
    return "";
  }
}

type CopyState = "idle" | "done" | "failed";

/** The clipboard can be unavailable (insecure origin, permissions): say so instead of doing nothing. */
async function copyText(text: string): Promise<CopyState> {
  try {
    if (!navigator.clipboard?.writeText) return "failed";
    await navigator.clipboard.writeText(text);
    return "done";
  } catch {
    return "failed";
  }
}

export function backupCodesText(codes: readonly string[]): string {
  return ["Townsy two-step sign-in backup codes", "Each code works once. Keep them somewhere safe.", "", ...codes, ""].join("\n");
}

/** The summary line and status of the section. */
export function TwoStepSummary({ status }: { status: TwoFactorStatusResponse }) {
  const staffNeedsIt = status.staff.hasStaffRole && status.staff.required && !status.enabled;
  return (
    <>
      {status.enabled ? (
        <p className="small" style={{ margin: 0 }} data-testid="two-step-on">
          <strong>Two-step sign-in is on.</strong> After your password, Townsy asks for a code from your authenticator app.
          {status.backupCodesLeft !== null ? ` Backup codes left: ${status.backupCodesLeft}.` : ""}
        </p>
      ) : (
        <p className="small" style={{ margin: 0 }} data-testid="two-step-off">
          Two-step sign-in is off. Turn it on to ask for a code from an authenticator app (such as Google Authenticator, Microsoft Authenticator or
          1Password) after your password, so a stolen password alone cannot open your account.
          {status.setupPending ? " A set-up was started but not finished; start again to get a new code." : ""}
        </p>
      )}
      {staffNeedsIt ? (
        <div className="notice notice-warning small" role="status" style={{ marginTop: 8 }} data-testid="two-step-staff-required">
          Your account has a Townsy staff role: staff tools work only after you turn on two-step sign-in.
        </div>
      ) : null}
      {status.enabled && status.backupCodesLeft !== null && status.backupCodesLeft <= 2 ? (
        <div className="notice notice-warning small" style={{ marginTop: 8 }} data-testid="two-step-few-codes">
          {status.backupCodesLeft === 0 ? "You have no backup codes left." : `Only ${status.backupCodesLeft} backup code${status.backupCodesLeft === 1 ? "" : "s"} left.`} To get new
          ones, turn two-step sign-in off and on again.
        </div>
      ) : null}
      {status.lockedUntil ? (
        <div className="notice notice-danger small" role="status" style={{ marginTop: 8 }}>
          Too many wrong codes were entered. Codes are refused until {formatUtcDateTime(status.lockedUntil)}.
        </div>
      ) : null}
    </>
  );
}

/** Step 2: scan or type the key, then enter the first code. */
export function TwoStepScanForm({
  totpUri,
  busy,
  error,
  onConfirm,
  onCancel,
}: {
  totpUri: string;
  busy: boolean;
  error: string | null;
  onConfirm(code: string): void;
  onCancel(): void;
}) {
  const [code, setCode] = useState("");
  const [copied, setCopied] = useState<CopyState>("idle");
  const key = manualKeyFromUri(totpUri);
  const copyKey = async () => setCopied(await copyText(key.replace(/\s+/g, "")));
  return (
    <form
      className="two-step-setup"
      data-movement-block="true"
      data-testid="two-step-scan"
      onSubmit={(e) => {
        e.preventDefault();
        onConfirm(code);
      }}
    >
      <QrCode text={totpUri} label="QR code for your authenticator app" />
      <div className="two-step-steps">
        <p className="small" style={{ margin: 0 }}>
          <strong>1.</strong> In your authenticator app, add an account and scan this QR code. On this phone, or if you cannot scan, choose “enter a setup
          key” and type:
        </p>
        <div className="manual-key" data-testid="two-step-manual-key">
          {key}
        </div>
        <div className="row">
          <button type="button" className="btn btn-sm" onClick={() => void copyKey()}>
            {copied === "done" ? "Key copied" : copied === "failed" ? "Copy not available: select the key" : "Copy key"}
          </button>
          <a className="btn btn-sm" href={totpUri}>
            Open in authenticator app
          </a>
        </div>
        <label className="field">
          <span>
            <strong>2.</strong> Enter the 6-digit code the app shows
          </span>
          <input
            className="code-input"
            type="text"
            name="one-time-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9 ]*"
            maxLength={8}
            required
            value={code}
            onChange={(e) => setCode(e.target.value)}
            aria-invalid={Boolean(error)}
            data-testid="two-step-setup-code"
          />
        </label>
        {error ? (
          <div className="notice notice-danger small" role="alert" data-testid="two-step-setup-error">
            {error}
          </div>
        ) : null}
        <div className="row">
          <button type="submit" className="btn btn-primary" disabled={busy} data-testid="two-step-setup-confirm">
            {busy ? "Checking…" : "Turn on two-step sign-in"}
          </button>
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
        </div>
      </div>
    </form>
  );
}

/** Step 3: the backup codes, shown once. */
export function BackupCodesView({ codes, onDone }: { codes: readonly string[]; onDone(): void }) {
  const [copied, setCopied] = useState<CopyState>("idle");
  const text = backupCodesText(codes);
  const copy = async () => setCopied(await copyText(text));
  const download = () => {
    const url = URL.createObjectURL(new Blob([text], { type: "text/plain;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "townsy-backup-codes.txt";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="stack" data-testid="two-step-backup-codes">
      <div className="notice notice-success small" role="status">
        <strong>Two-step sign-in is on.</strong> Save these backup codes now: <strong>they are shown only this once.</strong> Each one signs you in
        once if you lose your phone.
      </div>
      <ul className="backup-codes" aria-label="Backup codes">
        {codes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
      <div className="row">
        <button type="button" className="btn btn-sm" onClick={() => void copy()}>
          {copied === "done" ? "Copied" : copied === "failed" ? "Copy not available: use Download" : "Copy codes"}
        </button>
        <button type="button" className="btn btn-sm" onClick={download}>
          Download as a text file
        </button>
        <button type="button" className="btn btn-primary btn-sm" onClick={onDone} data-testid="two-step-codes-saved">
          I saved them
        </button>
      </div>
    </div>
  );
}

/** Turning it off: the password AND a current code or an unused backup code. */
export function TwoStepDisableForm({
  staff,
  busy,
  error,
  onSubmit,
  onCancel,
}: {
  staff: boolean;
  busy: boolean;
  error: string | null;
  onSubmit(password: string, code: string): void;
  onCancel(): void;
}) {
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  return (
    <form
      className="auth-forms"
      data-movement-block="true"
      data-testid="two-step-disable"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(password, code);
      }}
    >
      {staff ? (
        <div className="notice notice-warning small" role="status">
          Your account has a staff role: staff tools stop working until you turn two-step sign-in on again.
        </div>
      ) : null}
      <label className="field">
        Password
        <input type="password" name="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
      </label>
      <label className="field">
        Code from your authenticator app, or a backup code
        <input
          type="text"
          name="one-time-code"
          autoComplete="one-time-code"
          required
          maxLength={20}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          aria-invalid={Boolean(error)}
          data-testid="two-step-disable-code"
        />
      </label>
      {error ? (
        <div className="notice notice-danger small" role="alert" data-testid="two-step-disable-error">
          {error}
        </div>
      ) : null}
      <div className="row">
        <button type="submit" className="btn btn-danger" disabled={busy} data-testid="two-step-disable-confirm">
          {busy ? "Turning off…" : "Turn off two-step sign-in"}
        </button>
        <button type="button" className="btn" onClick={onCancel} disabled={busy}>
          Keep it on
        </button>
      </div>
    </form>
  );
}

/** Step 1: the password (Better Auth asks for it before creating a secret). */
export function TwoStepPasswordForm({ busy, error, onSubmit, onCancel }: { busy: boolean; error: string | null; onSubmit(password: string): void; onCancel(): void }) {
  const [password, setPassword] = useState("");
  return (
    <form
      className="auth-forms"
      data-movement-block="true"
      data-testid="two-step-password"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(password);
      }}
    >
      <label className="field">
        Your password, to confirm it's you
        <input type="password" name="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
      </label>
      {error ? (
        <div className="notice notice-danger small" role="alert" data-testid="two-step-password-error">
          {error}
        </div>
      ) : null}
      <div className="row">
        <button type="submit" className="btn btn-primary" disabled={busy} data-testid="two-step-password-continue">
          {busy ? "Preparing…" : "Continue"}
        </button>
        <button type="button" className="btn" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}

type Phase = { step: "idle" } | { step: "password" } | { step: "scan"; totpUri: string; backupCodes: string[] } | { step: "codes"; backupCodes: string[] } | { step: "disable" };

export function TwoStepSection({ signedIn }: { signedIn: boolean }) {
  const q = useTwoFactorStatusQuery(signedIn);
  const qc = useQueryClient();
  const invalidateIdentity = useInvalidateIdentity();
  const location = useLocation();
  const ref = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<Phase>({ step: "idle" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // `/account#two-step` (the staff "set it up" link) scrolls here once the section is there.
  const loaded = Boolean(q.data);
  useEffect(() => {
    if (loaded && location.hash === "#two-step") ref.current?.scrollIntoView({ block: "start" });
  }, [loaded, location.hash]);

  if (!signedIn) return null;
  const refresh = () => qc.invalidateQueries({ queryKey: twoFactorKeys.all });
  const go = (next: Phase) => {
    setPhase(next);
    setError(null);
    setNotice(null);
  };

  const onPassword = async (password: string) => {
    setBusy(true);
    setError(null);
    const res = await startTwoFactorSetup(password);
    setBusy(false);
    if (!res.ok) {
      setError(res.message);
      return;
    }
    go({ step: "scan", totpUri: res.totpUri, backupCodes: res.backupCodes });
  };

  const onConfirm = async (code: string) => {
    if (phase.step !== "scan") return;
    const clean = normalizeTotpCode(code);
    if (!TOTP_CODE_RE.test(clean)) {
      setError("Enter the 6-digit code from your authenticator app.");
      return;
    }
    setBusy(true);
    setError(null);
    const res = await confirmTwoFactorSetup(clean);
    setBusy(false);
    if (!res.ok) {
      setError(res.message ?? "That code is not right.");
      return;
    }
    // The server replaced this session; everything identity-bound is refetched.
    go({ step: "codes", backupCodes: phase.backupCodes });
    await invalidateIdentity();
  };

  const onDisable = async (password: string, code: string) => {
    const trimmed = code.trim();
    if (!TOTP_CODE_RE.test(normalizeTotpCode(trimmed)) && !BACKUP_CODE_RE.test(trimmed)) {
      setError("Enter the 6-digit code from your authenticator app, or one of your backup codes (like abcde-12345).");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await postTwoFactorDisable(password, trimmed);
      go({ step: "idle" });
      setNotice("Two-step sign-in is off.");
      await invalidateIdentity();
    } catch (err) {
      setError(twoFactorApiMessage(err, "Two-step sign-in could not be turned off."));
    } finally {
      setBusy(false);
    }
  };

  const status = q.data;
  return (
    <div className="section" id="two-step" ref={ref} data-testid="account-two-step">
      <div className="section-title">Two-step sign-in</div>
      {q.isPending ? <p className="muted small">Loading…</p> : null}
      {q.error ? (
        <p className="small error-text">
          Couldn't load your two-step sign-in settings: {errorMessage(q.error)}{" "}
          <button type="button" className="btn btn-sm" onClick={() => void q.refetch()}>
            Retry
          </button>
        </p>
      ) : null}
      {status && phase.step !== "codes" ? <TwoStepSummary status={status} /> : null}
      {notice ? (
        <div className="notice notice-info small" role="status" style={{ marginTop: 8 }}>
          {notice}
        </div>
      ) : null}
      {status && phase.step === "idle" ? (
        <div className="row" style={{ marginTop: 8 }}>
          {status.enabled ? (
            <button type="button" className="btn btn-sm" onClick={() => go({ step: "disable" })} data-testid="two-step-turn-off">
              Turn off two-step sign-in
            </button>
          ) : (
            <button type="button" className="btn btn-sm btn-primary" onClick={() => go({ step: "password" })} data-testid="two-step-turn-on">
              Set up two-step sign-in
            </button>
          )}
        </div>
      ) : null}
      <div style={{ marginTop: 8 }}>
        {phase.step === "password" ? <TwoStepPasswordForm busy={busy} error={error} onSubmit={(p) => void onPassword(p)} onCancel={() => go({ step: "idle" })} /> : null}
        {phase.step === "scan" ? <TwoStepScanForm totpUri={phase.totpUri} busy={busy} error={error} onConfirm={(c) => void onConfirm(c)} onCancel={() => go({ step: "idle" })} /> : null}
        {phase.step === "codes" ? (
          <BackupCodesView
            codes={phase.backupCodes}
            onDone={() => {
              go({ step: "idle" });
              void refresh();
            }}
          />
        ) : null}
        {phase.step === "disable" && status ? (
          <TwoStepDisableForm staff={status.staff.hasStaffRole} busy={busy} error={error} onSubmit={(p, c) => void onDisable(p, c)} onCancel={() => go({ step: "idle" })} />
        ) : null}
      </div>
    </div>
  );
}

/**
 * Sign-in step 2 for an account with two-step sign-in: the authenticator code, or a backup code instead. Some answers
 * end this attempt (too many wrong codes, timed out, locked); then the only way on is the email and password again.
 */
export function SignInCodeForm({
  onSignedIn,
  onRestart,
  initialMode = "totp",
  initialError = null,
}: {
  onSignedIn(how: "totp" | "backup"): void | Promise<void>;
  onRestart(): void;
  initialMode?: "totp" | "backup";
  initialError?: string | null;
}) {
  const [mode, setMode] = useState<"totp" | "backup">(initialMode);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(initialError);
  const [ended, setEnded] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const value = code.trim();
    if (mode === "totp" ? !TOTP_CODE_RE.test(normalizeTotpCode(value)) : !BACKUP_CODE_RE.test(value)) {
      setError(mode === "totp" ? "Enter the 6-digit code from your authenticator app." : "Enter one of your backup codes, like abcde-12345.");
      return;
    }
    setBusy(true);
    setError(null);
    const res = mode === "totp" ? await verifySignInCode(value) : await verifySignInBackupCode(value);
    setBusy(false);
    if (!res.ok) {
      setCode("");
      setError(res.message ?? "The code could not be checked.");
      if (res.code === "TOO_MANY_ATTEMPTS_REQUEST_NEW_CODE" || res.code === "INVALID_TWO_FACTOR_COOKIE" || res.code === "ACCOUNT_TEMPORARILY_LOCKED") setEnded(true);
      return;
    }
    await onSignedIn(mode);
  };
  return (
    <form className="auth-forms" onSubmit={(e) => void submit(e)} data-movement-block="true" data-testid="signin-code-form">
      <p className="small" style={{ margin: 0 }}>
        <strong>Two-step sign-in.</strong>{" "}
        {mode === "totp" ? "Enter the 6-digit code from your authenticator app." : "Enter one of the backup codes you saved when you set up two-step sign-in. Each one works once."}
      </p>
      <label className="field">
        {mode === "totp" ? "Code" : "Backup code"}
        <input
          className={mode === "totp" ? "code-input" : undefined}
          type="text"
          name="one-time-code"
          inputMode={mode === "totp" ? "numeric" : "text"}
          autoComplete="one-time-code"
          autoFocus
          required
          maxLength={mode === "totp" ? 8 : 20}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          disabled={ended}
          aria-invalid={Boolean(error)}
          data-testid="signin-code"
        />
      </label>
      {error ? (
        <div className="notice notice-danger small" role="alert" data-testid="signin-code-error">
          {error}
        </div>
      ) : null}
      <div className="row">
        {ended ? (
          <button type="button" className="btn btn-primary" onClick={onRestart} data-testid="signin-code-restart">
            Back to email and password
          </button>
        ) : (
          <>
            <button type="submit" className="btn btn-primary" disabled={busy} data-testid="signin-code-submit">
              {busy ? "Checking…" : "Sign in"}
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => {
                setMode(mode === "totp" ? "backup" : "totp");
                setCode("");
                setError(null);
              }}
              data-testid="signin-code-switch"
            >
              {mode === "totp" ? "Use a backup code instead" : "Use the authenticator app instead"}
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={onRestart}>
              Cancel
            </button>
          </>
        )}
      </div>
      <p className="hint" style={{ margin: 0 }}>
        Lost your phone and your backup codes? <Link to="/legal/terms">Contact Townsy</Link>: after confirming it's you, an operator can reset two-step
        sign-in.
      </p>
    </form>
  );
}
